<script setup lang="ts">
/**
 * ZplLabelPreview
 *
 * Draws the label's final ZPL (the same string the printer receives) on a
 * canvas with @tms-phoenix/label-preview, so the preview shows what prints.
 * The label sits as a white sheet in an NS-style panel (ns-web Generate Label:
 * .canvas-scroll / .label-canvas). Shows loading / error states, a warning when
 * content falls outside the printable area, a notice when no printer was chosen
 * (DPI fallback), and the parser warnings collapsed for support.
 *
 * Empty barcodes get a preview-only note (dashed box + caption naming the field,
 * e.g. "SEC not generated yet: prints an empty barcode") drawn on an overlay
 * canvas around the real printer output, so it is never mistaken for label ink.
 *
 * Not exported from the package barrel: it pulls in the label renderer, which
 * only apps that alias @tms-phoenix/label-preview can resolve. Import it by path:
 * `@tms-phoenix/ui-vue/components/ui-library/ZplLabelPreview.vue`.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  canvasSizePx,
  emptyBarcodeWarning,
  loadBarcodeEncoder,
  parseZpl,
  renderAnnotations,
  renderLabel,
  type BarcodeElement,
  type EmptyBarcode,
  type LabelAnnotation,
  type LabelCanvasContext,
} from '@tms-phoenix/label-preview';
import { useSharedTranslation } from '../../composables/useTranslationProvider';

/** A barcode field whose placeholder rendered empty (preview endpoint's empty_fields). */
export interface ZplEmptyField {
  key: string;
  fo: { x: number; y: number };
  command: string;
}

interface Props {
  /** Final rendered ZPL from the preview endpoint. */
  zpl: string;
  widthMm: number;
  heightMm: number;
  dpi: number;
  /** Screen pixels per CSS pixel at physical size. Default: fit the container width, at most 2×. */
  zoom?: number;
  /** 'fallback' when no printer was selected and the 203 dpi template was used. */
  dpiSource?: 'device' | 'fallback' | null;
  /** The caller is still fetching the ZPL. */
  loading?: boolean;
  /** The caller failed to fetch the ZPL; shown instead of the canvas. */
  error?: string | null;
  /** Template fields with no data; they print empty, so the preview shows them empty and warns. */
  missingFields?: string[];
  /** Barcode fields that rendered empty, to name them in the no-data notes. */
  emptyFields?: ZplEmptyField[];
}

const props = withDefaults(defineProps<Props>(), {
  zoom: undefined,
  dpiSource: null,
  loading: false,
  error: null,
  missingFields: () => [],
  emptyFields: () => [],
});

const { t, locale } = useSharedTranslation();

const MAX_AUTO_ZOOM = 2;
/** Panel padding on each side, in CSS px (NS .canvas-scroll: 20px). */
const FRAME_PADDING_PX = 20;

const container = ref<HTMLDivElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
const overlay = ref<HTMLCanvasElement | null>(null);
const containerWidth = ref(0);
const rendering = ref(false);
const renderError = ref(false);
const clipped = ref(false);
const warnings = ref<string[]>([]);
const cssSize = ref({ width: 0, height: 0 });

/** Bumped on every render; a render that finds a newer generation stops. */
let generation = 0;

const text = computed(() => {
  void locale.value; // reactivity trigger
  return {
    caption: t('ui.zplLabelPreview.caption', 'Preview of the printed label; fonts may differ slightly'),
    ariaLabel: t('ui.zplLabelPreview.ariaLabel', 'Label preview'),
    loading: t('ui.zplLabelPreview.loading', 'Loading preview…'),
    renderError: t('ui.zplLabelPreview.renderError', "The label preview couldn't be shown. You can still print."),
    clipped: t('ui.zplLabelPreview.clipped', 'Part of this label falls outside the printable area'),
    fallbackDpi: t('ui.zplLabelPreview.fallbackDpi', 'Select a printer for an exact preview'),
    details: t('ui.zplLabelPreview.details', 'Preview details'),
    missingFields: t('ui.zplLabelPreview.missingFields', 'These fields have no data and will print empty:'),
  };
});

/**
 * Note for an empty barcode: why it's empty (named field, SEC, or unknown) and
 * what the printer does (a Code 128 stub, or nothing).
 */
function emptyNote(field: ZplEmptyField | undefined, printsStub: boolean): string {
  let reason: string;
  if (!field) {
    reason = t('ui.zplLabelPreview.noData', 'No data');
  } else if (field.key === 'sec_encoded') {
    reason = t('ui.zplLabelPreview.secNotGenerated', 'SEC not generated yet');
  } else {
    const name = t(`ui.zplLabelPreview.field.${field.key}`, field.key);
    reason = t('ui.zplLabelPreview.fieldNoValue', '{{field}} has no value').replace('{{field}}', name);
  }
  const effect = printsStub
    ? t('ui.zplLabelPreview.printsEmptyBarcode', 'prints an empty barcode')
    : t('ui.zplLabelPreview.printsNothing', 'nothing prints');
  return `${reason}: ${effect}`;
}

const physicalWidthPx = computed(
  () => canvasSizePx({ widthMm: props.widthMm, heightMm: props.heightMm, dpi: props.dpi, zoom: 1 }).width,
);

const effectiveZoom = computed(() => {
  if (props.zoom && props.zoom > 0) return props.zoom;
  // Fit inside the panel's padding.
  const available = containerWidth.value - 2 * FRAME_PADDING_PX;
  if (available <= 0 || !physicalWidthPx.value) return 1;
  return Math.min(MAX_AUTO_ZOOM, available / physicalWidthPx.value);
});

const showCanvas = computed(() => !props.error && !renderError.value && !!props.zpl);
const isLoading = computed(() => props.loading || rendering.value);

async function render(): Promise<void> {
  const gen = ++generation;
  if (props.error || !props.zpl) {
    rendering.value = false;
    return;
  }
  // Snapshot the inputs: each render draws one consistent label, and the
  // generation check below is what stops an older one from drawing.
  const zpl = props.zpl;
  const geometry = { widthMm: props.widthMm, heightMm: props.heightMm, dpi: props.dpi };
  const zoom = effectiveZoom.value;
  const emptyFields = props.emptyFields;
  rendering.value = true;
  renderError.value = false;
  try {
    // Wait for fonts and the barcode encoder before touching the canvas, so a
    // newer render that started meanwhile wins and this one never draws.
    if (typeof document !== 'undefined' && document.fonts?.ready) await document.fonts.ready;
    await loadBarcodeEncoder();
    if (gen !== generation) return;

    const el = canvas.value;
    const ctx = el?.getContext('2d') as LabelCanvasContext | null | undefined;
    if (!el || !ctx) throw new Error('2D canvas unavailable');

    const { doc, warnings: parseWarnings } = parseZpl(zpl);
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    const buffer = canvasSizePx({ ...geometry, zoom: zoom * dpr });
    el.width = buffer.width;
    el.height = buffer.height;
    cssSize.value = { width: buffer.width / dpr, height: buffer.height / dpr };

    const result = await renderLabel(ctx, doc, { ...geometry, zoom: zoom * dpr });
    if (gen !== generation) return;

    // Preview-only notes for empty barcodes, matched to their field by ^FO.
    const annotations: LabelAnnotation[] = [];
    const noteFor = new Map<string, string>();
    for (const empty of result.emptyBarcodes) {
      const field = emptyFields.find((f) => f.fo.x === empty.origin.x && f.fo.y === empty.origin.y);
      const note = emptyNote(field, empty.ink !== null);
      const element = doc.elements[empty.index] as BarcodeElement;
      noteFor.set(emptyBarcodeWarning(element), `${note} (${element.x},${element.y})`);
      annotations.push({ box: inkBox(empty, element), text: note });
    }
    drawOverlay(buffer, annotations, geometry.dpi, zoom * dpr, dpr);

    clipped.value = result.extent.clipped.length > 0;
    // The details list uses the same wording as the notes.
    warnings.value = [...parseWarnings, ...result.warnings].map((w) => noteFor.get(w) ?? w);
  } catch {
    if (gen !== generation) return;
    renderError.value = true;
    clipped.value = false;
    warnings.value = [];
  } finally {
    if (gen === generation) rendering.value = false;
  }
}

/** The region a note surrounds: the printer's stub, or the field origin when nothing prints. */
function inkBox(empty: EmptyBarcode, element: BarcodeElement): LabelAnnotation['box'] {
  return empty.ink ?? { x: element.x, y: element.y, w: 0, h: 0 };
}

function drawOverlay(
  buffer: { width: number; height: number },
  annotations: LabelAnnotation[],
  dpi: number,
  zoom: number,
  dpr: number,
): void {
  const el = overlay.value;
  const ctx = el?.getContext('2d') as LabelCanvasContext | null | undefined;
  if (!el || !ctx) return;
  el.width = buffer.width;
  el.height = buffer.height;
  renderAnnotations(ctx, annotations, { dpi, zoom, pixelRatio: dpr });
}

watch(
  () => [props.zpl, props.widthMm, props.heightMm, props.dpi, props.error, props.emptyFields, effectiveZoom.value],
  () => { void render(); },
);

let observer: ResizeObserver | null = null;
onMounted(() => {
  containerWidth.value = container.value?.clientWidth ?? 0;
  if (typeof ResizeObserver !== 'undefined' && container.value) {
    observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (Math.abs(width - containerWidth.value) >= 1) containerWidth.value = width;
    });
    observer.observe(container.value);
  }
  void render();
});

onBeforeUnmount(() => {
  generation++;
  observer?.disconnect();
});

defineExpose({ render });
</script>

<template>
  <div
    ref="container"
    class="zpl-label-preview flex flex-col gap-2 w-full min-w-0"
  >
    <p
      v-if="dpiSource === 'fallback'"
      class="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1"
      data-testid="zpl-preview-fallback"
    >
      {{ text.fallbackDpi }}
    </p>

    <!-- NS-style panel: slate-200, 8px radius, 20px padding, soft inset shadow -->
    <div
      class="relative flex items-center justify-center rounded-lg bg-[#e2e8f0] p-5 min-h-24 shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
      data-testid="zpl-preview-frame"
    >
      <p
        v-if="error || renderError"
        class="text-sm text-red-700"
        role="alert"
        data-testid="zpl-preview-error"
      >
        {{ error || text.renderError }}
      </p>
      <div
        v-show="showCanvas"
        class="relative"
        :style="{ width: cssSize.width + 'px', height: cssSize.height + 'px' }"
      >
        <!-- The label: a white sheet with NS's drop shadow -->
        <canvas
          ref="canvas"
          role="img"
          :aria-label="text.ariaLabel"
          :style="{ width: cssSize.width + 'px', height: cssSize.height + 'px', imageRendering: 'pixelated' }"
          class="block bg-white rounded-[2px] shadow-[0_4px_16px_rgba(0,0,0,0.18),0_1px_4px_rgba(0,0,0,0.10)]"
          data-testid="zpl-preview-canvas"
        />
        <!-- Preview-only notes (not label ink) -->
        <canvas
          ref="overlay"
          aria-hidden="true"
          :style="{ width: cssSize.width + 'px', height: cssSize.height + 'px' }"
          class="absolute inset-0 pointer-events-none"
          data-testid="zpl-preview-annotations"
        />
      </div>
      <p
        v-if="isLoading && !error"
        class="absolute inset-0 flex items-center justify-center rounded-lg text-sm text-gray-500 bg-white/70"
        aria-live="polite"
        data-testid="zpl-preview-loading"
      >
        {{ text.loading }}
      </p>
    </div>

    <p
      v-if="clipped && showCanvas"
      class="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      role="status"
      data-testid="zpl-preview-clipped"
    >
      {{ text.clipped }}
    </p>

    <p
      v-if="missingFields.length && !error"
      class="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      role="status"
      data-testid="zpl-preview-missing-fields"
    >
      {{ text.missingFields }} {{ missingFields.join(', ') }}
    </p>

    <p class="text-xs text-gray-500">
      {{ text.caption }}
    </p>

    <details
      v-if="warnings.length && showCanvas"
      class="text-xs text-gray-500"
      data-testid="zpl-preview-warnings"
    >
      <summary class="cursor-pointer">
        {{ text.details }} ({{ warnings.length }})
      </summary>
      <ul class="list-disc pl-5 mt-1">
        <li
          v-for="(w, i) in warnings"
          :key="i"
        >
          {{ w }}
        </li>
      </ul>
    </details>
  </div>
</template>
